/**
 * DemoCountdownBumper — Banner persistente ≤3 giorni prima della scadenza demo (R60.19).
 *
 * Trigger: user.is_demo === true && daysLeft ∈ [0..3]
 * Non-dismissable (persistente): l'utente deve attivare il piano.
 * CTA: link diretto a /attiva-piano?plan=<demo_package> (Stripe self-service).
 * Countdown live: giorni + ore (aggiornato ogni minuto).
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, ArrowRight, Clock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

function computeCountdown(expiresAtISO) {
  if (!expiresAtISO) return null;
  try {
    const exp = new Date(expiresAtISO + "T23:59:59");
    const ms = exp.getTime() - Date.now();
    if (ms <= 0) return { days: 0, hours: 0, expired: true };
    const days = Math.floor(ms / (1000 * 60 * 60 * 24));
    const hours = Math.floor((ms % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    return { days, hours, expired: false };
  } catch {
    return null;
  }
}

export default function DemoCountdownBumper() {
  const { user } = useAuth();
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 60 * 1000);
    return () => clearInterval(id);
  }, []);

  if (!user?.is_demo || !user?.demo_expires_at) return null;
  if (!["admin", "operator"].includes(user.role)) return null;

  const cd = computeCountdown(user.demo_expires_at);
  if (!cd || cd.expired) return null; // scaduta gestita da DemoExpiredModal
  if (cd.days > 3) return null; // troppo presto per allarmare

  // eslint-disable-next-line no-unused-vars
  const _ = tick;

  const isCritical = cd.days === 0;
  const preferredPlan = ["base", "plus", "executive"].includes(user.package_choice) ? user.package_choice : "plus";
  const ctaHref = `/attiva-piano?plan=${preferredPlan}&billing=yearly`;

  const countdownStr = cd.days > 0
    ? `${cd.days} giorn${cd.days === 1 ? "o" : "i"}${cd.hours > 0 ? ` e ${cd.hours} h` : ""}`
    : cd.hours > 0
      ? `${cd.hours} ore`
      : "meno di 1 ora";

  return (
    <div
      className={`sticky top-0 z-40 w-full ${isCritical ? "bg-red-600" : "bg-amber-500"} text-white shadow-md`}
      data-testid="demo-countdown-bumper"
    >
      <div className="max-w-6xl mx-auto px-4 py-2.5 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2.5 flex-1 min-w-0">
          {isCritical ? (
            <AlertTriangle className="w-5 h-5 shrink-0 animate-pulse" />
          ) : (
            <Clock className="w-5 h-5 shrink-0" />
          )}
          <div className="min-w-0">
            <p className="text-sm font-bold leading-tight">
              {isCritical ? "Ultimo giorno di demo!" : `La demo scade tra ${countdownStr}`}
            </p>
            <p className="text-[11px] opacity-90 leading-tight hidden sm:block">
              Attiva ora il tuo piano per non perdere i dati inseriti · Pagamento sicuro Stripe · Nessun rinnovo automatico
            </p>
          </div>
        </div>
        <Link
          to={ctaHref}
          className={`shrink-0 inline-flex items-center gap-1.5 h-9 px-4 rounded-lg text-[13px] font-bold bg-white ${
            isCritical ? "text-red-700 hover:bg-red-50" : "text-amber-800 hover:bg-amber-50"
          } active:scale-[.98] transition shadow-sm`}
          data-testid="demo-countdown-cta"
        >
          Attiva ora <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </div>
  );
}
